import {useQueryClient} from '@tanstack/react-query';
import React, {useCallback, useState} from 'react';
import {Box, Stack, styled} from '@mui/material';
import {
  Avatar,
  Body,
  Button,
  Headline,
  Icon,
  Tabs,
  Tooltip,
  designSystemToken,
  Label,
} from '@lightricks/react-design-system';
import {TabData} from '@lightricks/react-design-system/dist/components/tabs/TabsProps';
import getEngagementRateString from '@/utils/getEngagementRateString';
import getFollowersCountString from '@/utils/getFollowersCountString';
import getPlatformFromIdentityProvider from '@/utils/getIPlatformFromIdentityProvider';
import translate from '@/utils/translate';
import type {CreatorBrandSafetyVettingStatus} from '@/types/creatorBrandSafetyVetting';
import type {ProposalAnswer} from '@/api/fetchers/proposal';
import type {CampaignCreatorRow} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import CreatorEmailCopy from '@/components/creator-email-copy';
import Link from '@/components/link';
import SafeCollabChip from '@/components/safe-collab-chip';
import SideDrawer from '@/components/side-drawer';
import SocialNetworkIcon from '@/components/social-network-icon';
import useConversationDetailsQuery from '@/hooks/queries/use-conversation-details-query';
import {CONVERSATION_DETAILS_QUERY_KEY} from '@/hooks/queries/use-conversation-details-query/useConversationDetailsQuery';
import useCreatorProfileQuery from '@/hooks/queries/use-creator-profile-query';
import usePaidGigQuery from '@/hooks/queries/use-paid-gig-query';
import styles from './CreatorSidePanel.module.scss';
import ApplicationTab from './components/ApplicationTab';
import MessagesTab from './components/MessagesTab';
import QuickViewTab from './components/QuickViewTab';
import {SIDE_PANEL_TAB, SidePanelTab} from './types';
import useResizableDrawerWidth from './useResizableDrawerWidth';

const UnreadDot = styled(Label)(() => ({
  width: '16px',
  height: '16px',
  borderRadius: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  margin: 0,
  backgroundColor: designSystemToken('semantic.bg.neutral'),
  color: `${designSystemToken('semantic.fg.white')} !important`,
  lineHeight: 1,
  letterSpacing: 0,
}));

export type CreatorSidePanelProps = {
  testID?: string;
  isOpen: boolean;
  creator: CampaignCreatorRow | null;
  conversationId: string;
  applicationQuestions?: ProposalAnswer[];
  brandId: string;
  brandName: string;
  campaignName?: string;
  campaignId?: string;
  campaignNetwork?: string | null;
  isNegotiatedTermsVisible?: boolean;
  campaignUsageRightsPeriodInMonths?: number | null;
  defaultTab: SidePanelTab;
  onClose: () => void;
  onViewFullProfile: (profileId: string) => void;
};

function CreatorSidePanel({
  testID = 'creator-side-panel',
  isOpen,
  creator,
  conversationId,
  applicationQuestions = [],
  brandId,
  brandName,
  campaignName,
  campaignId,
  campaignNetwork,
  isNegotiatedTermsVisible = false,
  campaignUsageRightsPeriodInMonths,
  defaultTab,
  onClose,
  onViewFullProfile,
}: CreatorSidePanelProps) {
  const queryClient = useQueryClient();
  const {unreadMessagesCount} = useConversationDetailsQuery(
    conversationId,
    brandId
  );
  const [activeTab, setActiveTab] = useState<SidePanelTab>(defaultTab);
  const {width: panelWidth, handleResizePointerDown} =
    useResizableDrawerWidth();

  // Fetch the creator's profile while the panel is open. It powers two things:
  // the admin-only email (CSMs reach out directly) and the campaign-network
  // social link next to the stats — the campaign-creators list endpoint only
  // returns aggregate followers/engagement, so the per-network profile URL
  // comes from here. Fetched for all users since the social link is shown to
  // everyone; the email is still gated to admins at render time.
  const {profile} = useCreatorProfileQuery({
    profileId: creator?.profileId ?? null,
    brandId,
    enabled: Boolean(isOpen && creator?.profileId),
  });

  const {data: paidGig} = usePaidGigQuery(
    isNegotiatedTermsVisible ? (creator?.paidGigId ?? null) : null
  );

  // Show only the campaign's social network (e.g. IG for an IG campaign), and
  // only when the creator has a linkable identity for it. Provider variants
  // (instagram_business, tiktok_v2, …) are normalized to a single platform.
  const campaignPlatform = campaignNetwork
    ? getPlatformFromIdentityProvider(campaignNetwork)
    : null;
  const campaignIdentity =
    campaignPlatform && profile?.identities
      ? (profile.identities.find(
          (identity) =>
            Boolean(identity.profilePageUrl) &&
            getPlatformFromIdentityProvider(identity.provider) ===
              campaignPlatform
        ) ?? null)
      : null;

  // Option C: invalidate conversation details on close so unread indicators
  // refresh on next open, without clearing them while the user is reading.
  const handleClose = useCallback(() => {
    if (conversationId && brandId) {
      queryClient.invalidateQueries({
        queryKey: [CONVERSATION_DETAILS_QUERY_KEY, conversationId, brandId],
      });
    }
    onClose();
  }, [onClose, queryClient, conversationId, brandId]);

  const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
    setActiveTab(value as SidePanelTab);
  };

  const handleViewFullProfile = () => {
    if (creator) {
      onViewFullProfile(creator.profileId);
    }
  };

  const handleViewMessages = () => {
    setActiveTab(SIDE_PANEL_TAB.Messages);
  };

  const tabsData: TabData[] = [
    {label: translate('Quick view'), value: SIDE_PANEL_TAB.QuickView},
    {label: translate('Application'), value: SIDE_PANEL_TAB.Application},
    {
      label: translate('Messages'),
      value: SIDE_PANEL_TAB.Messages,
      ...(unreadMessagesCount > 0
        ? {
            endAdornment: (
              <UnreadDot size="xs" data-testid="messages-unread-dot">
                {unreadMessagesCount}
              </UnreadDot>
            ),
          }
        : {}),
    },
  ];

  if (!creator) {
    return null;
  }

  const subtitle = [creator.age, creator.location].filter(Boolean).join(' | ');

  return (
    <SideDrawer
      testID={testID}
      isOpen={isOpen}
      onClose={handleClose}
      withoutBackdrop
      drawerClassName={styles.sideDrawer}
      drawerStyle={{width: panelWidth}}
    >
      <Stack className={styles.container}>
        <Box
          className={styles.resizeHandle}
          onPointerDown={handleResizePointerDown}
          data-testid={`${testID}-resize-handle`}
          role="separator"
          aria-orientation="vertical"
          aria-label={translate('Resize panel')}
          aria-valuenow={panelWidth}
        />
        <Stack direction="row" className={styles.header}>
          <Avatar
            size="2xl"
            src={creator.avatarUrl ?? ''}
            type={creator.avatarUrl ? 'picture' : 'initials'}
            initials={creator.creatorName?.charAt(0) ?? ''}
          />
          <Stack className={styles.headerInfo}>
            <Box className={styles.nameBlock}>
              <Tooltip
                title={translate('View full profile')}
                type="light"
                arrow
                describeChild
              >
                <Box
                  component="button"
                  type="button"
                  className={styles.displayNameLink}
                  onClick={handleViewFullProfile}
                  data-testid="creator-side-panel-name-link"
                >
                  <Headline size="md" className={styles.displayName}>
                    {creator.creatorName}
                  </Headline>
                  <Icon
                    size="small"
                    appearance="neutral"
                    name="Actions-Arrow-Long-Forward"
                    className={styles.displayNameLinkIcon}
                  />
                </Box>
              </Tooltip>
              {subtitle ? (
                <Body size="sm" className={styles.subtitle}>
                  {subtitle}
                </Body>
              ) : null}
              <CreatorEmailCopy
                email={profile?.email}
                testID="creator-side-panel"
              />
            </Box>
            <Box display="inline-flex">
              <SafeCollabChip
                status={
                  (creator.brandSafetyVettingDecision ??
                    'ready_for_review') as CreatorBrandSafetyVettingStatus
                }
              />
            </Box>
            {/* Content campaigns have no social network, so followers /
                engagement aren't tracked (always 0) and there's no social
                profile to link to — hide the whole stats row. */}
            {campaignNetwork ? (
              <Stack direction="row" className={styles.stats}>
                {campaignIdentity ? (
                  <Link
                    testID="creator-social-link"
                    className={styles.statsSocialLink}
                    to={campaignIdentity.profilePageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    keepBrandIdIfPresent={false}
                    aria-label={translate('Open social profile')}
                  >
                    <SocialNetworkIcon
                      name={campaignIdentity.provider}
                      width={24}
                      height={24}
                    />
                  </Link>
                ) : null}
                <Tooltip title={translate('followers')} type="light" arrow>
                  <span>
                    <Label size="md">
                      {getFollowersCountString(creator.followers)}
                    </Label>
                  </span>
                </Tooltip>
                <Body
                  size="md"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  ·
                </Body>
                <Tooltip title={translate('Eng rate')} type="light" arrow>
                  <span>
                    <Label size="md">
                      {getEngagementRateString(creator.engagementRate)}
                    </Label>
                  </span>
                </Tooltip>
              </Stack>
            ) : null}
          </Stack>
        </Stack>

        <Button
          className={styles.closeButton}
          size="small"
          appearance="neutral"
          mode="plain"
          onClick={handleClose}
        >
          <Icon size="small" appearance="neutral" name="Actions-Close-Normal" />
        </Button>

        <Tabs
          testID={`${testID}-tabs`}
          size="large"
          appearance="neutral"
          onChange={handleTabChange}
          value={activeTab}
          tabsData={tabsData}
          showBorderBottom
          variant="standard"
        />

        {activeTab === SIDE_PANEL_TAB.QuickView ? (
          <QuickViewTab
            portfolioImages={creator.portfolioImages}
            unreadMessagesCount={unreadMessagesCount}
            onViewMessages={handleViewMessages}
          />
        ) : null}

        {activeTab === SIDE_PANEL_TAB.Application ? (
          <ApplicationTab
            questions={applicationQuestions}
            isNegotiatedTermsVisible={isNegotiatedTermsVisible}
            negotiatedCostInCents={creator.costInCents}
            usageRightsPeriodInMonths={
              paidGig?.contentAgreementTimePeriodInMonths ??
              campaignUsageRightsPeriodInMonths
            }
          />
        ) : null}

        {activeTab === SIDE_PANEL_TAB.Messages ? (
          <MessagesTab
            conversationId={conversationId}
            brandId={brandId}
            brandName={brandName}
            creatorName={creator.creatorName ?? ''}
            campaignName={campaignName}
            campaignId={campaignId}
          />
        ) : null}
      </Stack>
    </SideDrawer>
  );
}

export default CreatorSidePanel;
